import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { settingsService } from '../services/api';
import { useWidgetVisibility } from '../context/WidgetVisibilityContext';
import { widgetsGroupedByLocation, type WidgetLocation } from '../utils/widgetRegistry';
import './Dashboard.css';
import './Settings.css';

const Settings: React.FC = () => {
  const { hostel } = useAuth();
  const [loading, setLoading] = useState(true);
  const [savingPassword, setSavingPassword] = useState(false);
  const [savingAdvance, setSavingAdvance] = useState(false);
  const [savingRules, setSavingRules] = useState(false);
  const [savingReminders, setSavingReminders] = useState(false);
  const [reminderForm, setReminderForm] = useState({
    paymentRemindersEnabled: true,
    reminderStartDay: '1',
    reminderEndDay: '28',
    reminderCadenceDays: '3',
    reminderRunTime: '11:00',
    billingDueDay: '5',
  });
  const [defaultPassword, setDefaultPassword] = useState('');
  const [hasStoredDefault, setHasStoredDefault] = useState(false);
  const [defaultAdvance, setDefaultAdvance] = useState('0');
  const [hostelRules, setHostelRules] = useState('');
  const [widgetPrefs, setWidgetPrefs] = useState<Record<string, boolean>>({});
  const [savingWidgets, setSavingWidgets] = useState(false);
  const { refresh: refreshWidgetContext, setPreferences: setContextWidgetPrefs } = useWidgetVisibility();

  useEffect(() => {
    if (!hostel?.id) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const [defaults, rules, widgets, billing] = await Promise.all([
          settingsService.getTenantDefaults(hostel.id),
          settingsService.getHostelRules(hostel.id),
          settingsService.getDisplayWidgets(hostel.id),
          settingsService.getBillingRules(hostel.id),
        ]);
        if (cancelled) return;
        setHasStoredDefault(defaults.hasDefault);
        setDefaultPassword(defaults.defaultNewTenantPassword || '');
        setDefaultAdvance(String(defaults.defaultNewTenantAdvance ?? 0));
        setHostelRules(rules.hostelRules || '');
        setWidgetPrefs(widgets);
        setContextWidgetPrefs(widgets);
        const hour = String(billing.reminderRunHour ?? 11).padStart(2, '0');
        const minute = String(billing.reminderRunMinute ?? 0).padStart(2, '0');
        setReminderForm({
          paymentRemindersEnabled: Boolean(billing.paymentRemindersEnabled),
          reminderStartDay: String(billing.reminderStartDay ?? 1),
          reminderEndDay: String(billing.reminderEndDay ?? 28),
          reminderCadenceDays: String(billing.reminderCadenceDays ?? 3),
          reminderRunTime: `${hour}:${minute}`,
          billingDueDay: String(billing.billingDueDay ?? 5),
        });
      } catch {
        if (!cancelled) toast.error('Could not load settings');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [hostel?.id]);

  const handleSavePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!hostel?.id) return;
    if (defaultPassword.length > 0 && defaultPassword.length < 6) {
      toast.error('Default password must be at least 6 characters, or leave empty to clear.');
      return;
    }
    setSavingPassword(true);
    try {
      const d = await settingsService.updateTenantDefaults(hostel.id, {
        defaultNewTenantPassword: defaultPassword.trim(),
      });
      setHasStoredDefault(d.hasDefault);
      setDefaultPassword(d.defaultNewTenantPassword || '');
      toast.success('Default password saved.');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || 'Failed to save password');
    } finally {
      setSavingPassword(false);
    }
  };

  const handleClearPassword = async () => {
    if (!hostel?.id) return;
    if (!window.confirm('Remove the default password for new tenants?')) return;
    setSavingPassword(true);
    try {
      const d = await settingsService.updateTenantDefaults(hostel.id, { defaultNewTenantPassword: '' });
      setHasStoredDefault(d.hasDefault);
      setDefaultPassword('');
      toast.success('Default password removed.');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || 'Failed to clear');
    } finally {
      setSavingPassword(false);
    }
  };

  const handleSaveAdvance = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!hostel?.id) return;
    const adv = Number(defaultAdvance);
    if (!Number.isFinite(adv) || adv < 0) {
      toast.error('Default advance must be a number ≥ 0');
      return;
    }
    setSavingAdvance(true);
    try {
      const d = await settingsService.updateTenantDefaults(hostel.id, { defaultNewTenantAdvance: adv });
      setDefaultAdvance(String(d.defaultNewTenantAdvance ?? 0));
      toast.success('Default advance saved.');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || 'Failed to save advance');
    } finally {
      setSavingAdvance(false);
    }
  };

  const toggleWidget = (id: string) => {
    setWidgetPrefs((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const setAllWidgetsInLocation = (location: WidgetLocation, enabled: boolean) => {
    setWidgetPrefs((prev) => {
      const next = { ...prev };
      for (const group of widgetsGroupedByLocation(location)) {
        for (const w of group.widgets) {
          next[w.id] = enabled;
        }
      }
      return next;
    });
  };

  const handleSaveWidgets = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!hostel?.id) return;
    setSavingWidgets(true);
    try {
      const saved = await settingsService.updateDisplayWidgets(hostel.id, widgetPrefs);
      setWidgetPrefs(saved);
      setContextWidgetPrefs(saved);
      await refreshWidgetContext();
      toast.success('Dashboard & reports widgets updated.');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || 'Failed to save widget preferences');
    } finally {
      setSavingWidgets(false);
    }
  };

  const renderWidgetToggles = (location: WidgetLocation, title: string) => (
    <div className="settings-widget-block" key={location}>
      <div className="settings-widget-block__head">
        <h3>{title}</h3>
        <div className="settings-widget-block__actions">
          <button type="button" className="btn btn-sm" onClick={() => setAllWidgetsInLocation(location, true)}>
            Select all
          </button>
          <button type="button" className="btn btn-sm" onClick={() => setAllWidgetsInLocation(location, false)}>
            Clear all
          </button>
        </div>
      </div>
      {widgetsGroupedByLocation(location).map((group) => (
        <div key={group.group} className="settings-widget-group">
          <h4>{group.group}</h4>
          <ul className="settings-widget-list">
            {group.widgets.map((w) => (
              <li key={w.id}>
                <label className="settings-widget-check">
                  <input
                    type="checkbox"
                    checked={widgetPrefs[w.id] !== false}
                    onChange={() => toggleWidget(w.id)}
                  />
                  <span className="settings-widget-check__text">
                    <strong>{w.label}</strong>
                    <span>{w.description}</span>
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );

  const dayOptions = Array.from({ length: 28 }, (_, i) => i + 1);

  const handleSaveReminders = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!hostel?.id) return;

    const start = Number(reminderForm.reminderStartDay);
    const end = Number(reminderForm.reminderEndDay);
    const cadence = Number(reminderForm.reminderCadenceDays);
    const dueDay = Number(reminderForm.billingDueDay);
    const timeMatch = /^(\d{1,2}):(\d{2})$/.exec(reminderForm.reminderRunTime.trim());

    if (!Number.isFinite(start) || start < 1 || start > 28) {
      toast.error('Start day must be between 1 and 28');
      return;
    }
    if (!Number.isFinite(end) || end < 1 || end > 28) {
      toast.error('End day must be between 1 and 28');
      return;
    }
    if (end < start) {
      toast.error('End day cannot be before start day');
      return;
    }
    if (!Number.isFinite(cadence) || cadence < 1 || cadence > 28) {
      toast.error('Reminder frequency must be between 1 and 28 days');
      return;
    }
    if (!Number.isFinite(dueDay) || dueDay < 1 || dueDay > 28) {
      toast.error('Rent due day must be between 1 and 28');
      return;
    }
    if (!timeMatch) {
      toast.error('Choose a valid run time');
      return;
    }

    const reminderRunHour = Number(timeMatch[1]);
    const reminderRunMinute = Number(timeMatch[2]);
    if (reminderRunHour > 23 || reminderRunMinute > 59) {
      toast.error('Choose a valid run time');
      return;
    }

    setSavingReminders(true);
    try {
      const saved = await settingsService.updateBillingRules(hostel.id, {
        paymentRemindersEnabled: reminderForm.paymentRemindersEnabled,
        reminderStartDay: start,
        reminderEndDay: end,
        reminderCadenceDays: cadence,
        reminderRunHour,
        reminderRunMinute,
        billingDueDay: dueDay,
      });
      const hour = String(saved.reminderRunHour ?? 11).padStart(2, '0');
      const minute = String(saved.reminderRunMinute ?? 0).padStart(2, '0');
      setReminderForm({
        paymentRemindersEnabled: Boolean(saved.paymentRemindersEnabled),
        reminderStartDay: String(saved.reminderStartDay ?? 1),
        reminderEndDay: String(saved.reminderEndDay ?? 28),
        reminderCadenceDays: String(saved.reminderCadenceDays ?? 3),
        reminderRunTime: `${hour}:${minute}`,
        billingDueDay: String(saved.billingDueDay ?? 5),
      });
      toast.success('Payment reminder settings saved.');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || 'Failed to save reminder settings');
    } finally {
      setSavingReminders(false);
    }
  };

  const handleSaveRules = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!hostel?.id) return;
    setSavingRules(true);
    try {
      const rules = await settingsService.updateHostelRules(hostel.id, { hostelRules: hostelRules.trim() });
      setHostelRules(rules.hostelRules || '');
      toast.success('Hostel rules saved.');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || 'Failed to save hostel rules');
    } finally {
      setSavingRules(false);
    }
  };

  if (!hostel) {
    return (
      <div className="dashboard-container settings-page">
        <p>Choose a hostel context to manage settings.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="dashboard-container settings-page">
        <div className="loading-container">Loading settings…</div>
      </div>
    );
  }

  return (
    <div className="dashboard-container settings-page">
      <header className="settings-header dashboard-header">
        <h1>Settings</h1>
      </header>

      <div className="settings-sections">
        <section className="settings-card">
          <h2>Default password</h2>
          <p className="settings-card-desc">
            Used for <em>Quick user</em> creation, or <em>Full user</em> when the password field is left blank.
            Each tenant signs in with their phone number. Stored as plain text for staff reference only.
          </p>
          {hasStoredDefault ? (
            <p className="settings-status-ok">A default password is currently configured.</p>
          ) : null}
          <form onSubmit={handleSavePassword}>
            <label className="settings-field">
              <span>Default password for new tenants</span>
              <input
                type="text"
                autoComplete="off"
                value={defaultPassword}
                onChange={(e) => setDefaultPassword(e.target.value)}
                placeholder="Min 6 characters"
              />
            </label>
            <div className="settings-actions">
              <button type="submit" className="btn btn-primary" disabled={savingPassword}>
                {savingPassword ? 'Saving…' : 'Save password'}
              </button>
              {hasStoredDefault ? (
                <button type="button" className="btn" disabled={savingPassword} onClick={handleClearPassword}>
                  Clear password
                </button>
              ) : null}
            </div>
          </form>
        </section>

        <section className="settings-card">
          <h2>Default advance</h2>
          <p className="settings-card-desc">
            Pre-fills the advance amount (₹) on the Quick user form. You can still change it for each tenant.
          </p>
          <form onSubmit={handleSaveAdvance}>
            <label className="settings-field">
              <span>Default advance (₹)</span>
              <input
                type="number"
                min={0}
                step="1"
                inputMode="numeric"
                value={defaultAdvance}
                onChange={(e) => setDefaultAdvance(e.target.value)}
              />
            </label>
            <div className="settings-actions">
              <button type="submit" className="btn btn-primary" disabled={savingAdvance}>
                {savingAdvance ? 'Saving…' : 'Save advance'}
              </button>
            </div>
          </form>
        </section>

        <section className="settings-card settings-card--wide settings-card--reminders">
          <div className="settings-reminders-head">
            <div>
              <h2>Payment reminders</h2>
              <p className="settings-card-desc">
                Automated WhatsApp/SMS nudges for tenants who have not paid rent for the current month.
                Reminders run only when <strong>WhatsApp credits</strong> are available (see Credits page).
              </p>
            </div>
            <label className="settings-reminder-toggle">
              <input
                type="checkbox"
                checked={reminderForm.paymentRemindersEnabled}
                onChange={(e) =>
                  setReminderForm((p) => ({ ...p, paymentRemindersEnabled: e.target.checked }))
                }
              />
              <span className="settings-reminder-toggle__track" aria-hidden />
              <span className="settings-reminder-toggle__label">
                {reminderForm.paymentRemindersEnabled ? 'Reminders ON' : 'Reminders OFF'}
              </span>
            </label>
          </div>

          <form onSubmit={handleSaveReminders}>
            <div className="settings-reminders-grid">
              <label className="settings-field">
                <span>From day of month</span>
                <select
                  value={reminderForm.reminderStartDay}
                  onChange={(e) => setReminderForm((p) => ({ ...p, reminderStartDay: e.target.value }))}
                  disabled={!reminderForm.paymentRemindersEnabled}
                >
                  {dayOptions.map((d) => (
                    <option key={`start-${d}`} value={String(d)}>
                      {d}
                    </option>
                  ))}
                </select>
              </label>
              <label className="settings-field">
                <span>To day of month</span>
                <select
                  value={reminderForm.reminderEndDay}
                  onChange={(e) => setReminderForm((p) => ({ ...p, reminderEndDay: e.target.value }))}
                  disabled={!reminderForm.paymentRemindersEnabled}
                >
                  {dayOptions.map((d) => (
                    <option key={`end-${d}`} value={String(d)}>
                      {d}
                    </option>
                  ))}
                </select>
              </label>
              <label className="settings-field">
                <span>Every (days)</span>
                <select
                  value={reminderForm.reminderCadenceDays}
                  onChange={(e) => setReminderForm((p) => ({ ...p, reminderCadenceDays: e.target.value }))}
                  disabled={!reminderForm.paymentRemindersEnabled}
                >
                  {dayOptions.map((d) => (
                    <option key={`cadence-${d}`} value={String(d)}>
                      Every {d} day{d === 1 ? '' : 's'}
                    </option>
                  ))}
                </select>
              </label>
              <label className="settings-field">
                <span>Run at (time)</span>
                <input
                  type="time"
                  value={reminderForm.reminderRunTime}
                  onChange={(e) => setReminderForm((p) => ({ ...p, reminderRunTime: e.target.value }))}
                  disabled={!reminderForm.paymentRemindersEnabled}
                />
              </label>
              <label className="settings-field">
                <span>Rent due day (in message)</span>
                <select
                  value={reminderForm.billingDueDay}
                  onChange={(e) => setReminderForm((p) => ({ ...p, billingDueDay: e.target.value }))}
                  disabled={!reminderForm.paymentRemindersEnabled}
                >
                  {dayOptions.map((d) => (
                    <option key={`due-${d}`} value={String(d)}>
                      {d}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <p className="settings-reminders-hint">
              Example: From <strong>1</strong> to <strong>28</strong>, every <strong>3</strong> days at{' '}
              <strong>{reminderForm.reminderRunTime}</strong> sends on days 1, 4, 7, 10… within that window.
            </p>
            <div className="settings-actions">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={savingReminders}
              >
                {savingReminders ? 'Saving…' : 'Save reminder settings'}
              </button>
            </div>
          </form>
        </section>

        <section className="settings-card settings-card--wide">
          <h2>Dashboard &amp; reports widgets</h2>
          <p className="settings-card-desc">
            Choose which cards and charts appear on the <strong>Dashboard</strong> and{' '}
            <strong>Reports</strong> pages for this hostel. Unchecked items are hidden for all admins.
          </p>
          <form onSubmit={handleSaveWidgets}>
            {renderWidgetToggles('dashboard', 'Dashboard')}
            {renderWidgetToggles('reports', 'Reports')}
            <div className="settings-actions">
              <button type="submit" className="btn btn-primary" disabled={savingWidgets}>
                {savingWidgets ? 'Saving…' : 'Save widget visibility'}
              </button>
            </div>
          </form>
        </section>

        <section className="settings-card settings-card--wide">
          <h2>Hostel rules</h2>
          <p className="settings-card-desc">
            House rules, timings, visitor policy, and other guidelines for tenants. You can share this text with
            residents or display it in onboarding.
          </p>
          <form onSubmit={handleSaveRules}>
            <label className="settings-field">
              <span>Rules &amp; policies</span>
              <textarea
                value={hostelRules}
                onChange={(e) => setHostelRules(e.target.value)}
                placeholder="e.g. Gate closes at 10 PM. No cooking in rooms. Visitors allowed until 8 PM on weekends…"
                rows={8}
              />
            </label>
            <div className="settings-actions">
              <button type="submit" className="btn btn-primary" disabled={savingRules}>
                {savingRules ? 'Saving…' : 'Save hostel rules'}
              </button>
            </div>
          </form>
        </section>
      </div>
    </div>
  );
};

export default Settings;
